import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as productService from '../lib/services/productService';
import './Home.css';

/**
 * Home Component - Responsive Landing Page
 * 
 * Features:
 * - Dynamic screen size detection (Mobile: ≤480px, Tablet: 481-1024px, Desktop: >1024px)
 * - Optimized image rendering based on device (loads only visible slides on mobile)
 * - Touch/swipe support for mobile navigation
 * - Lazy loading for non-active slideshow images
 * - Hardware-accelerated animations for smooth transitions
 * - Responsive slideshow timing (6s on mobile, 5s on desktop)
 * - First image preloading for better performance
 */

const Home = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [slideshowImages, setSlideshowImages] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState({});
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth <= 480,
    isTablet: window.innerWidth > 480 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024
  });
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const navigate = useNavigate();

  // Minimum swipe distance (in px) to trigger navigation
  const minSwipeDistance = 50;

  // Detect screen size changes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isMobile: width <= 480,
        isTablet: width > 480 && width <= 1024,
        isDesktop: width > 1024
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Dynamically load all images from the slideshow folder
    const loadImages = () => {
      try {
        const context = require.context('../../public/slideshow', false, /\.(jpg|jpeg|png|gif|webp)$/i);
        const images = context.keys().map(key => `/slideshow/${key.replace('./', '')}`);
        setSlideshowImages(images);
        
        // Preload the first image for better performance
        if (images.length > 0) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = images[0];
          document.head.appendChild(link);
        }
      } catch (error) {
        console.error('Error loading slideshow images:', error);
        setSlideshowImages([]);
      }
    };


    loadImages();
    fetchProducts();
  }, []);


  const fetchProducts = async () => {
    try {
      const products = await productService.getProducts();

      // Group products by category and take first 2 from each
      const grouped = {};
      products.forEach(product => {
        if (!grouped[product.category]) {
          grouped[product.category] = [];
        }
        if (grouped[product.category].length < 2) {
          grouped[product.category].push(product);
        }
      });
      setCategoryProducts(grouped);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleCategoryClick = (category) => {
    navigate('/products', { state: { selectedCategory: category } });
  };

  // Touch handlers for swipe navigation on mobile
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      // Swipe left - go to next slide
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % slideshowImages.length);
    } else if (isRightSwipe) {
      // Swipe right - go to previous slide
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? slideshowImages.length - 1 : prevIndex - 1
      );
    }
  };

  // Determine if an image should be rendered based on screen size
  const shouldRenderImage = useCallback((index) => {
    // On mobile, only render current, previous, and next images for performance
    if (screenSize.isMobile) {
      const prevIndex = currentImageIndex === 0 ? slideshowImages.length - 1 : currentImageIndex - 1;
      const nextIndex = (currentImageIndex + 1) % slideshowImages.length;
      return index === currentImageIndex || index === prevIndex || index === nextIndex;
    }
    // On tablet and desktop, render all images
    return true;
  }, [screenSize.isMobile, currentImageIndex, slideshowImages.length]);

  // Get responsive image sizes based on screen width
  const getImageSizes = () => {
    if (screenSize.isMobile) {
      return '100vw';
    } else if (screenSize.isTablet) {
      return '100vw';
    }
    return '100vw';
  };

  useEffect(() => {
    if (slideshowImages.length > 0) {
      // Adjust slideshow speed based on screen size
      const intervalTime = screenSize.isMobile ? 6000 : 5000; // Slightly slower on mobile
      
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % slideshowImages.length);
      }, intervalTime);


      return () => clearInterval(interval);
    }
  }, [slideshowImages, screenSize.isMobile]);


  return (
    <div className="home">
      {/* Hero Section with Slideshow */}
      <section className="hero">
        {slideshowImages.length > 0 ? (
          <div 
            className="slideshow"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {slideshowImages.map((imagePath, index) => {
              const isActive = index === currentImageIndex;
              const shouldRender = shouldRenderImage(index);
              
              // Skip rendering non-visible images on mobile for performance
              if (!shouldRender) return null;

              return (
                <div
                  key={index}
                  className={`slideshow-item ${isActive ? 'active' : ''}`}
                >
                  <img 
                    src={imagePath} 
                    alt={`Slide ${index + 1}`}
                    loading={isActive ? 'eager' : 'lazy'}
                    sizes={getImageSizes()}
                    style={{
                      willChange: isActive ? 'opacity' : 'auto'
                    }}
                    decoding="async"
                  />
                </div>
              );
            })}
            <div className="slideshow-indicators">
              {slideshowImages.map((_, index) => (
                <span
                  key={index}
                  className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentImageIndex(index)
                  }}
                  role="button"
                  aria-label={`Go to slide ${index + 1}`}
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setCurrentImageIndex(index);
                    }
                  }}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="no-slideshow-images">
            <p>Add images to the public/slideshow folder to display the banner</p>
          </div>
        )}
      </section>


      {/* Products Section */}
      <section className="section products-section">
        <div className="container">
          <h2 className="section-title">Our Products</h2>
          <p className="section-subtitle">
            A tasty blend of tradition and health. Light, crispy and the perfect health-friendly snack!
          </p>

          <div className="categories-grid">
            {Object.entries(categoryProducts).map(([category, products]) => (
              <div key={category} className="category-block">
                <div className="category-header">
                  <h3>{category}</h3>
                  <button 
                    onClick={() => handleCategoryClick(category)}
                    className="btn-view-category"
                  >
                    View More →
                  </button>
                </div>
                <div className="category-products">
                  {products.map(product => (
                    <Link 
                      key={product.id}
                      to={`/products/${product.id}`}
                      className="product-card-mini"
                    >
                      <div className="product-image-mini">
                        {product.image_url && (
                          <img src={product.image_url} alt={product.name} />
                        )}
                        {product.gluten_free && (
                          <span className="gluten-free-tag-mini">Gluten Free</span>
                        )}
                      </div>
                      <div className="product-info-mini">
                        <h4>{product.name}</h4>
                        {product.weight && product.weight > 0 && (
                          <p style={{ fontSize: '0.8em', color: '#666', margin: '3px 0' }}>📦 {product.weight}g</p>
                        )}
                        {product.discounted_price && product.discounted_price < product.price ? (
                          <div className="price-mini">
                            <span className="old-price-mini">kr {Number(product.price).toFixed(2)}</span>
                            <span className="new-price-mini">kr {Number(product.discounted_price).toFixed(2)}</span>
                          </div>
                        ) : (
                          <p className="price-mini">kr {Number(product.price).toFixed(2)}</p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/products" className="btn btn-outline">
              View All Products
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};


export default Home;